/**
 * Device Service
 *
 * API calls for device registration, management, settings, WiFi, Bluetooth, and FOTA
 * Uses path-based multi-tenancy: /api/orgs/{orgId}/...
 */
import apiClient from './apiClient';

// Placeholder IDs - will come from auth context after login
const DEFAULT_ORG_ID = 1;
const DEFAULT_HIERARCHY_NODE_ID = 1;

const deviceService = {
  // =============================================================================
  // DEVICE REGISTRATION & CRUD
  // =============================================================================

  /**
   * List devices for an org/hierarchy
   * @param {number} orgId - Organization ID
   * @param {number} hierarchyNodeId - Hierarchy node ID
   * @param {Object} options - Additional options (siteId, limit, offset)
   * @returns {Promise<Object>} Paged response with devices
   */
  listDevices: async (orgId = DEFAULT_ORG_ID, hierarchyNodeId = DEFAULT_HIERARCHY_NODE_ID, options = {}) => {
    const params = {
      siteId: options.siteId,
      limit: options.limit || 50,
      offset: options.offset || 0,
      sortBy: options.sortBy || 'deviceId',
      sortOrder: options.sortOrder || 'desc'
    };
    if (hierarchyNodeId && hierarchyNodeId !== DEFAULT_HIERARCHY_NODE_ID) {
      params.hierarchyNodeId = hierarchyNodeId;
    }
    const response = await apiClient.get(`/api/orgs/${orgId}/devices`, { params });
    return response.data;
  },

  /**
   * Get device details by ID
   * @param {number} deviceId - Device ID
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} Device details
   */
  getDevice: async (deviceId, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/devices/${deviceId}`);
    return response.data;
  },

  /**
   * Update device metadata
   * @param {number} deviceId - Device ID
   * @param {Object} deviceData - Fields to update
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} Updated device details
   */
  updateDevice: async (deviceId, deviceData, orgId = DEFAULT_ORG_ID) => {
    const tierPackage = deviceData.tierPackage || deviceData.tier;
    const formattedTier = tierPackage
      ? (tierPackage.toUpperCase().includes('_TIER') ? tierPackage.toUpperCase() : `${tierPackage.toUpperCase()}_TIER`)
      : undefined;

    const payload = {
      deviceType: deviceData.deviceType,
      deviceName: deviceData.deviceName,
      serialNumber: deviceData.serialNumber,
      hierarchyNodeId: deviceData.hierarchyNodeId,
      siteId: deviceData.siteId,
      configurationProfileId: deviceData.configurationProfileId || deviceData.templateProfileId || deviceData.template,
      tierPackage: formattedTier
    };
    const response = await apiClient.patch(`/api/orgs/${orgId}/devices/${deviceId}`, payload);
    return response.data;
  },

  /**
   * Delete a device
   * @param {number} deviceId - Device ID
   * @param {number} orgId - Organization ID
   */
  deleteDevice: async (deviceId, orgId = DEFAULT_ORG_ID) => {
    await apiClient.delete(`/api/orgs/${orgId}/devices/${deviceId}`);
  },

  /**
   * Register a new device
   * @param {Object} deviceData - Device registration data
   * @returns {Promise<Object>} Registered device with ID
   */
  registerDevice: async (deviceData) => {
    const orgId = deviceData.orgId || DEFAULT_ORG_ID;
    const tierPackage = deviceData.tierPackage || deviceData.tier;
    const formattedTier = tierPackage ?
      (tierPackage.toUpperCase().includes('_TIER') ? tierPackage.toUpperCase() : `${tierPackage.toUpperCase()}_TIER`) :
      'STANDARD_TIER';

    const response = await apiClient.post(`/api/orgs/${orgId}/devices`, {
      orgId,
      hierarchyNodeId: deviceData.hierarchyNodeId || null,
      siteId: deviceData.siteId || deviceData.site || null,
      deviceType: deviceData.deviceType,
      deviceName: deviceData.deviceName,
      serialNumber: deviceData.serialNumber,
      configurationProfileId: deviceData.configurationProfileId || deviceData.templateProfileId || deviceData.template || null,
      tierPackage: formattedTier,
    });
    return response.data;
  },

  /**
   * Register devices in bulk from CSV (async)
   * Submits CSV, receives job ID, polls until completion.
   * @param {number} orgId - Organization ID
   * @param {number} hierarchyNodeId - Hierarchy node ID (optional, auto-resolved)
   * @param {File} file - CSV file (max 700KB, max 3000 rows)
   * @param {number} siteId - Optional site ID
   * @param {Function} onProgress - Optional callback(job) for progress updates
   * @returns {Promise<Object>} Final bulk registration results {successes, failures, jobId}
   */
  registerDevicesBulk: async (orgId, hierarchyNodeId, file, siteId, onProgress) => {
    const resolvedOrgId = orgId || DEFAULT_ORG_ID;

    // Client-side file size validation (700 KB)
    const MAX_FILE_SIZE = 700 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      throw new Error('CSV file exceeds maximum allowed size of 700 KB');
    }

    const formData = new FormData();
    formData.append('file', file);

    const params = {};
    if (hierarchyNodeId) params.hierarchyNodeId = hierarchyNodeId;
    if (siteId) params.siteId = siteId;

    // Submit — expect HTTP 202 Accepted
    // NOTE: Do NOT set Content-Type manually here. axios detects FormData and
    // sets "multipart/form-data; boundary=<auto>" automatically. Manually
    // setting it strips the boundary, causing Spring to fail parsing the parts.
    const submitResponse = await apiClient.post(
      `/api/orgs/${resolvedOrgId}/devices/bulk`,
      formData,
      { params }
    );

    const { jobId, statusUrl } = submitResponse.data;
    if (!jobId) {
      throw new Error('No job ID returned from bulk registration');
    }

    // Poll for completion
    const pollUrl = statusUrl || `/api/orgs/${resolvedOrgId}/devices/bulk-jobs/${jobId}`;
    const POLL_INTERVAL_MS = 2000;
    const MAX_POLL_ATTEMPTS = 150; // 5 minutes max

    for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
      await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));

      const pollResponse = await apiClient.get(pollUrl);
      const job = pollResponse.data;

      if (onProgress) {
        onProgress(job);
      }

      if (job.status === 'COMPLETED' || job.status === 'FAILED') {
        if (job.status === 'FAILED') {
          throw new Error(job.errorMessage || 'Bulk registration job failed');
        }
        // Return in the same shape as the old synchronous response
        return {
          successes: job.successes || [],
          failures: job.failures || [],
          jobId: job.jobId
        };
      }
    }

    throw new Error('Bulk registration timed out. Check job status: ' + jobId);
  },

  /**
   * Search devices by various criteria
   * @param {Object} criteria - Search criteria
   * @param {number} limit - Max results
   * @param {number} offset - Offset for pagination
   * @returns {Promise<Object>} Paged search results
   */
  searchDevices: async (criteria, limit = 50, offset = 0) => {
    const orgId = criteria.orgId || DEFAULT_ORG_ID;
    const response = await apiClient.get(`/api/orgs/${orgId}/devices/search`, {
      params: {
        hierarchyNodeId: criteria.hierarchyNodeId,
        deviceType: criteria.deviceType,
        serialNumber: criteria.serialNumber,
        deviceName: criteria.deviceName,
        status: criteria.status,
        query: criteria.query,
        limit,
        offset
      }
    });
    return response.data;
  },

  // =============================================================================
  // DEVICE SETTINGS
  // =============================================================================

  /**
   * Get device settings
   * @param {number} deviceId - Device ID
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} Device settings
   */
  getDeviceSettings: async (deviceId, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/device-management/devices/${deviceId}/settings`);
    return response.data;
  },

  /**
   * Update device settings
   * @param {number} deviceId - Device ID
   * @param {Object} settings - Settings to update
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} Updated settings
   */
  updateDeviceSettings: async (deviceId, settings, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.patch(`/api/orgs/${orgId}/device-management/devices/${deviceId}/settings`, settings);
    return response.data;
  },

  /**
   * Clone settings from one device to others
   * @param {number} sourceDeviceId - Source device ID
   * @param {Array<number>} targetDeviceIds - Target device IDs
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} Clone results
   */
  cloneDeviceSettings: async (sourceDeviceId, targetDeviceIds, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.post(`/api/orgs/${orgId}/device-management/devices/clone`, {
      sourceDeviceId,
      targetDeviceIds
    });
    return response.data;
  },

  /**
   * Bulk update device settings
   * @param {Object} updateRequest - Bulk update request
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} Bulk update results
   */
  bulkUpdateDeviceSettings: async (updateRequest, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.post(`/api/orgs/${orgId}/device-management/devices/bulk-update`, updateRequest);
    return response.data;
  },

  // =============================================================================
  // BUTTON CONFIGURATION
  // =============================================================================

  /**
   * Get button configuration for a site/hierarchy
   * @param {number} siteId - Site ID
   * @param {number} hierarchyNodeId - Hierarchy node ID
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} Button configuration
   */
  getButtonConfiguration: async (siteId, hierarchyNodeId, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/device-management/button-config`, {
      params: { siteId, hierarchyNodeId }
    });
    return response.data;
  },

  /**
   * Save button configuration
   * @param {Object} config - Button configuration
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} Saved configuration
   */
  saveButtonConfiguration: async (config, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.put(`/api/orgs/${orgId}/device-management/button-config`, config);
    return response.data;
  },

  // =============================================================================
  // SITE DEVICE DEFAULTS
  // =============================================================================

  /**
   * Get default device settings for a site
   * @param {number} siteId - Site ID
   * @param {number} hierarchyNodeId - Hierarchy node ID
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} Site device defaults
   */
  getSiteDeviceDefaults: async (siteId, hierarchyNodeId, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/device-management/site-defaults`, {
      params: { siteId, hierarchyNodeId }
    });
    return response.data;
  },

  /**
   * Save default device settings for a site
   * @param {Object} defaults - Default settings
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} Saved defaults
   */
  saveSiteDeviceDefaults: async (defaults, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.put(`/api/orgs/${orgId}/device-management/site-defaults`, defaults);
    return response.data;
  },

  // =============================================================================
  // DEVICE CONFIGURATION PROFILES
  // =============================================================================

  listDeviceProfiles: async (orgId = DEFAULT_ORG_ID, { includeConfigStatus = false } = {}) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/device-management/profiles`, {
      params: includeConfigStatus ? { includeConfigStatus: true } : undefined,
    });
    return response.data;
  },

  getDeviceProfile: async (profileId, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/device-management/profiles/${profileId}`);
    return response.data;
  },

  createDeviceProfile: async (payload, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.post(`/api/orgs/${orgId}/device-management/profiles`, payload);
    return response.data;
  },

  updateDeviceProfile: async (profileId, payload, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.put(`/api/orgs/${orgId}/device-management/profiles/${profileId}`, payload);
    return response.data;
  },

  deleteDeviceProfile: async (profileId, orgId = DEFAULT_ORG_ID) => {
    await apiClient.delete(`/api/orgs/${orgId}/device-management/profiles/${profileId}`);
  },

  // =============================================================================
  // WIFI NETWORKS
  // =============================================================================

  /**
   * List WiFi networks for an org/site
   * @param {number} orgId - Organization ID
   * @param {number} siteId - Optional site ID
   * @returns {Promise<Array>} List of WiFi networks
   */
  listWifiNetworks: async (orgId = DEFAULT_ORG_ID, siteId) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/device-management/wifi-networks`, {
      params: { siteId }
    });
    return response.data;
  },

  /**
   * Get a WiFi network by ID
   * @param {number} networkId - WiFi network ID
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} WiFi network details
   */
  getWifiNetwork: async (networkId, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/device-management/wifi-networks/${networkId}`);
    return response.data;
  },

  /**
   * Create a WiFi network
   * @param {Object} networkData - WiFi network data
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} Created network
   */
  createWifiNetwork: async (networkData, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.post(`/api/orgs/${orgId}/device-management/wifi-networks`, networkData);
    return response.data;
  },

  /**
   * Update a WiFi network
   * @param {number} networkId - WiFi network ID
   * @param {Object} networkData - Updated network data
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} Updated network
   */
  updateWifiNetwork: async (networkId, networkData, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.put(`/api/orgs/${orgId}/device-management/wifi-networks/${networkId}`, networkData);
    return response.data;
  },

  /**
   * Delete a WiFi network
   * @param {number} networkId - WiFi network ID
   * @param {number} orgId - Organization ID
   * @returns {Promise<void>}
   */
  deleteWifiNetwork: async (networkId, orgId = DEFAULT_ORG_ID) => {
    await apiClient.delete(`/api/orgs/${orgId}/device-management/wifi-networks/${networkId}`);
  },

  /**
   * Assign a WiFi network to multiple devices
   * @param {number} orgId - Organization ID
   * @param {number} networkId - WiFi network ID
   * @param {Array<number>} deviceIds - Device IDs
   * @param {number} priority - Optional priority
   * @returns {Promise<Object>} Bulk assignment result
   */
  assignWifiNetworkToDevices: async (orgId, networkId, deviceIds, priority) => {
    const response = await apiClient.post(`/api/orgs/${orgId}/device-management/devices/wifi-networks/assign`, {
      networkId,
      deviceIds,
      orgId,
      priority
    });
    return response.data;
  },

  /**
   * Remove a WiFi network from multiple devices
   * @param {number} orgId - Organization ID
   * @param {number} networkId - WiFi network ID
   * @param {Array<number>} deviceIds - Device IDs
   * @returns {Promise<Object>} Bulk removal result
   */
  removeWifiNetworkFromDevices: async (orgId, networkId, deviceIds) => {
    const response = await apiClient.post(`/api/orgs/${orgId}/device-management/devices/wifi-networks/remove`, {
      networkId,
      deviceIds,
      orgId,
    });
    return response.data;
  },

  // =============================================================================
  // BLUETOOTH DEVICES
  // =============================================================================

  /**
   * List Bluetooth devices for a device
   * @param {number} deviceId - Device ID
   * @param {number} orgId - Organization ID
   * @returns {Promise<Array>} List of Bluetooth devices
   */
  listBluetoothDevices: async (deviceId, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/device-management/devices/${deviceId}/bluetooth-devices`);
    return response.data;
  },

  /**
   * Get a Bluetooth device by ID
   * @param {number} bluetoothDeviceId - Bluetooth device ID
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} Bluetooth device details
   */
  getBluetoothDevice: async (bluetoothDeviceId, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/device-management/bluetooth-devices/${bluetoothDeviceId}`);
    return response.data;
  },

  /**
   * Add a Bluetooth device
   * @param {Object} deviceData - Bluetooth device data
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} Created Bluetooth device
   */
  createBluetoothDevice: async (deviceData, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.post(`/api/orgs/${orgId}/device-management/bluetooth-devices`, deviceData);
    return response.data;
  },

  /**
   * Delete a Bluetooth device
   * @param {number} bluetoothDeviceId - Bluetooth device ID
   * @param {number} orgId - Organization ID
   * @returns {Promise<void>}
   */
  deleteBluetoothDevice: async (bluetoothDeviceId, orgId = DEFAULT_ORG_ID) => {
    await apiClient.delete(`/api/orgs/${orgId}/device-management/bluetooth-devices/${bluetoothDeviceId}`);
  },

  // =============================================================================
  // SOFTWARE UPDATES (FOTA)
  // =============================================================================

  /**
   * List software updates for an org
   * @param {number} orgId - Organization ID
   * @param {number} limit - Max results
   * @param {number} offset - Offset for pagination
   * @returns {Promise<Object>} Paged list of software updates
   */
  listSoftwareUpdates: async (orgId = DEFAULT_ORG_ID, limit = 50, offset = 0) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/software-updates`, {
      params: { limit, offset }
    });
    return response.data;
  },

  /**
   * Get a software update by ID
   * @param {number} updateId - Software update ID
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} Software update details
   */
  getSoftwareUpdate: async (updateId, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/software-updates/${updateId}`);
    return response.data;
  },

  /**
   * Get the latest software update for an org
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} Latest software update
   */
  getLatestSoftwareUpdate: async (orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/software-updates/latest`);
    return response.data;
  },

  /**
   * Get org-level automatic update window settings
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} Automatic update window settings, with automaticUpdatesEnabled=false and suggested 01:00-04:00 values when no settings have been saved
   */
  getAutomaticUpdateWindow: async (orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/software-updates/settings/automatic-window`);
    return response.data;
  },

  /**
   * Get firmware update health summary for an organization.
   * Returns healthPercentage, healthLabel, totalDevices, upToDate, updateAvailable,
   * updating, offline, and error counts — all calculated server-side.
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} Update health summary
   */
  getUpdateHealth: async (orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/software-updates/update-health`);
    return response.data;
  },


  /**
   * Save org-level automatic update window settings
   * @param {Object} settings - { automaticUpdatesEnabled, updateWindowFrom, updateWindowTo }
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} Persisted automatic update window settings
   */
  saveAutomaticUpdateWindow: async (settings, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.put(`/api/orgs/${orgId}/software-updates/settings/automatic-window`, settings);
    return response.data;
  },

  /**
   * Push OTA package updates to eligible outdated devices.
   * @param {number} orgId - Organization ID
   * @param {?number} siteId - Optional site ID filter
   * @returns {Promise<Object>} Created OTA update jobs summary
   */
  pushOutdatedUpdates: async (orgId = DEFAULT_ORG_ID, siteId = null) => {
    const payload = {};
    if (siteId !== null && siteId !== undefined) {
      payload.siteId = siteId;
    }
    const response = await apiClient.post(`/api/orgs/${orgId}/software-updates/jobs/push-outdated`, payload);
    return response.data;
  },

  /**
   * Get an update job by ID
   * @param {number} jobId - Update job ID
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} Update job details
   */
  getUpdateJob: async (jobId, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/software-updates/jobs/${jobId}`);
    return response.data;
  },

  /**
   * List update jobs for an org
   * @param {number} orgId - Organization ID
   * @param {number} limit - Max results
   * @param {number} offset - Offset for pagination
   * @returns {Promise<Object>} Paged list of update jobs
   */
  listUpdateJobs: async (orgId = DEFAULT_ORG_ID, limit = 50, offset = 0, siteId = null) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/software-updates/jobs`, {
      params: { limit, offset, siteId: siteId || undefined }
    });
    return response.data;
  },

  /**
   * Get items for an update job
   * @param {number} jobId - Update job ID
   * @param {number} orgId - Organization ID
   * @returns {Promise<Array>} List of job items with per-device status
   */
  getUpdateJobItems: async (jobId, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/software-updates/jobs/${jobId}/items`);
    return response.data;
  },

  /**
   * Cancel an update job
   * @param {number} jobId - Update job ID
   * @param {number} orgId - Organization ID
   * @returns {Promise<void>}
   */
  cancelUpdateJob: async (jobId, orgId = DEFAULT_ORG_ID) => {
    await apiClient.post(`/api/orgs/${orgId}/software-updates/jobs/${jobId}/cancel`);
  },
};

export default deviceService;
